// ============================================================================
// useResolvedSeedOptions — selected-value label resolution for entity selects
// ============================================================================
// THE BUG THIS EXISTS TO PREVENT
//
// SyutSelect resolves a value's LABEL by looking its id up in a pool of options
// it already holds: the async cache from loadOptions(''), plus whatever
// `seedOptions` the caller passed. Both are a PAGE — the first `pageSize` rows.
// A master with more rows than that has records whose id simply isn't in the
// pool, so an edit form renders the field BLANK even though the parent's state
// holds the correct id.
//
// Not hypothetical: the test tenant has 1263 clients against a 500-row page, so
// every client ranked past 500 edited blank — on the Engagement master, on FSA
// and on GST alike, because all three go through these wrappers. 7 of 9
// engagements were affected.
//
// Raising pageSize only moves the cliff and makes every dropdown open slower.
// Fetch the ONE selected record by id instead and add it to the seeds. One
// request, only when the id can't already be resolved, and never on the common
// path where it can.
//
// Every Syut*Select shares this hook so the fix cannot be half-applied: a new
// wrapper that forgets it is a wrapper that silently blanks on edit.

import { useEffect, useMemo, useRef, useState } from 'react';

export interface SeedOption {
  value: string;
  label: string;
}

/**
 * @param value         The currently-selected id, if any.
 * @param seedOptions   Seeds the caller already supplied (often the edited
 *                      record's own denormalised name — always preferred over
 *                      a fetch, since it costs nothing).
 * @param fetchOption   Resolves one id to an option. Must be referentially
 *                      stable (wrap in useCallback) or it will refetch on
 *                      every render.
 * @returns `seedOptions` plus the resolved option when one was needed.
 */
export function useResolvedSeedOptions(
  value: string | undefined,
  seedOptions: SeedOption[] | undefined,
  fetchOption: (id: string) => Promise<SeedOption | null>,
): SeedOption[] | undefined {
  const [resolved, setResolved] = useState<SeedOption | null>(null);
  // Guards a re-render from re-issuing a lookup that is already in flight.
  const inFlightRef = useRef<string | null>(null);

  const seedHasValue = useMemo(
    () => Boolean(value) && (seedOptions ?? []).some((o) => o.value === value),
    [value, seedOptions],
  );

  useEffect(() => {
    if (!value || seedHasValue || resolved?.value === value) return;
    if (inFlightRef.current === value) return;
    inFlightRef.current = value;

    let cancelled = false;
    fetchOption(value)
      .then((option) => {
        if (cancelled || !option) return;
        setResolved(option);
      })
      .catch(() => {
        // Best-effort. A deleted or inaccessible record leaves the field blank,
        // which is the honest rendering — better than inventing a label.
      })
      .finally(() => {
        if (inFlightRef.current === value) inFlightRef.current = null;
      });

    return () => { cancelled = true; };
  }, [value, seedHasValue, resolved, fetchOption]);

  return useMemo(() => {
    const base = seedOptions ?? [];
    if (!resolved || base.some((o) => o.value === resolved.value)) {
      return seedOptions;
    }
    return [...base, resolved];
  }, [seedOptions, resolved]);
}

export default useResolvedSeedOptions;
